import { useState } from 'react';
import { Link, useNavigate } from 'react-router';
import { useTranslation } from 'react-i18next';
import { Button, Dialog, Input, Price, Select } from '@artosera/ui';
import {
  comptesArtistesApi,
  useTableauComptesArtistes,
  useRelevesArtistes,
  useCreerReleveArtiste,
  usePreparerRelevesArtistes,
  useRapprocherComptesArtistes,
} from '../../api/comptesArtistes.js';
import { dansLaPorteeCourante } from '../../api/cles.js';
import { enregistrerFichier } from '../../api/client.js';
import { galerieALaction, galerieAModule } from '../../auth/sujet.js';
import { useShellContext } from '../shell/context.js';
import { Field } from '../Field.js';
import { FormulaireAvance } from './FormulaireAvance.js';
import { messageCompte } from './compteUi.js';
import styles from './ComptesArtistes.module.css';

export function ComptesArtistesPage() {
  const { galerieSlug, galerie } = useShellContext();
  const { t, i18n } = useTranslation(['regie', 'commun']);
  const label = (cle: string) => t(`regie:comptesArtistes.${cle}`);
  const langue = i18n.language === 'en' ? 'en' : 'fr';
  const navigate = useNavigate();
  const tableau = useTableauComptesArtistes(galerieSlug);
  const releves = useRelevesArtistes(galerieSlug);
  const creer = useCreerReleveArtiste(galerieSlug);
  const lot = usePreparerRelevesArtistes(galerieSlug);
  const rapprocher = useRapprocherComptesArtistes(galerieSlug);
  const [dialogue, setDialogue] = useState<'avance' | 'releve' | 'lot' | null>(null);
  const [artisteId, setArtisteId] = useState('');
  const [debut, setDebut] = useState('');
  const [fin, setFin] = useState('');
  const [du, setDu] = useState('');
  const [au, setAu] = useState('');
  const [erreur, setErreur] = useState('');
  const [resultat, setResultat] = useState('');
  const peutEcrire = galerieALaction(galerie, 'artist-accounts:write');
  const peutValider = galerieALaction(galerie, 'artist-accounts:validate');
  const artistes = tableau.data?.soldes ?? [];
  let titreDialogue = label('nouveauReleve');
  if (dialogue === 'avance') titreDialogue = label('nouvelleAvance');
  if (dialogue === 'lot') titreDialogue = label('preparer');
  const nom = (id: string) => artistes.find((a) => a.artisteId === id)?.nom ?? id;
  async function exporter() {
    setErreur('');
    try {
      const fichier = await dansLaPorteeCourante((a) =>
        comptesArtistesApi.exporter(
          galerieSlug,
          { ...(du ? { du } : {}), ...(au ? { au } : {}) },
          a,
        ),
      );
      enregistrerFichier(fichier, 'comptes-artistes.csv');
    } catch (e) {
      setErreur(messageCompte(e, t('commun:erreurReseau')));
    }
  }
  return (
    <div data-ui="page" className={styles.page}>
      <header className={styles.entete}>
        <h1>{label('titre')}</h1>
        <div className={styles.actions}>
          {peutEcrire && (
            <>
              <Button
                onClick={() => {
                  setErreur('');
                  setDialogue('lot');
                }}
              >
                {label('preparer')}
              </Button>
              <Button
                onClick={() => {
                  setErreur('');
                  setDialogue('releve');
                }}
              >
                {label('nouveauReleve')}
              </Button>
              <Button
                onClick={() => {
                  setErreur('');
                  setDialogue('avance');
                }}
              >
                {label('nouvelleAvance')}
              </Button>
            </>
          )}
          {peutValider && (
            <Button
              loading={rapprocher.isPending}
              onClick={() => {
                setErreur('');
                void rapprocher
                  .mutateAsync({})
                  .then(() => setResultat(label('rapprochementDemande')))
                  .catch((e: unknown) => setErreur(messageCompte(e, t('commun:erreurReseau'))));
              }}
            >
              {label('rapprocher')}
            </Button>
          )}
        </div>
      </header>
      {erreur && !dialogue && <p role="alert">{erreur}</p>}
      {resultat && <p role="status">{resultat}</p>}
      {tableau.isLoading && <p aria-busy="true">{t('commun:chargement')}</p>}
      {tableau.isError && (
        <p role="alert">{messageCompte(tableau.error, t('commun:erreurReseau'))}</p>
      )}
      {tableau.data && (
        <>
          <section className={styles.section}>
            <h2>{label('soldes')}</h2>
            <div className={styles.tableauEnveloppe}>
              <table className={styles.tableau}>
                <thead>
                  <tr>
                    <th scope="col">{label('artiste')}</th>
                    <th scope="col">{label('solde')}</th>
                  </tr>
                </thead>
                <tbody>
                  {artistes.map((a) => (
                    <tr key={a.artisteId}>
                      <td>
                        <Link to={`/${galerieSlug}/artistes/${a.artisteId}`}>{a.nom}</Link>
                      </td>
                      <td>
                        <Price cents={a.soldeCentimes} lang={langue} />
                      </td>
                    </tr>
                  ))}
                </tbody>
              </table>
            </div>
          </section>
          <section className={styles.section}>
            <h2>{label('releves')}</h2>
            <dl className={styles.totaux}>
              {Object.entries(tableau.data.compteurs).map(([cle, count]) => (
                <div key={cle}>
                  <dt>{label(cle)}</dt>
                  <dd>{count}</dd>
                </div>
              ))}
            </dl>
            {releves.isLoading && <p aria-busy="true">{t('commun:chargement')}</p>}
            {releves.isError && (
              <p role="alert">{messageCompte(releves.error, t('commun:erreurReseau'))}</p>
            )}
            <ul>
              {releves.data?.releves.map((r) => (
                <li key={r.id}>
                  <Link to={`/${galerieSlug}/comptes-artistes/releves/${r.id}`}>
                    {nom(r.artisteId)} · {r.numero ?? label('etats.brouillon')} ·{' '}
                    {r.debut.slice(0, 10)} → {r.fin.slice(0, 10)}
                  </Link>{' '}
                  · {label(`etats.${r.etat}`)}
                </li>
              ))}
            </ul>
          </section>
          <section className={styles.section}>
            <h2>{label('avancesEnCours')}</h2>
            <ul>
              {tableau.data.avances.map((a) => (
                <li key={a.id}>
                  <Link to={`/${galerieSlug}/comptes-artistes/avances/${a.id}`}>
                    {nom(a.artisteId)} · {label(`natures.${a.nature}`)}
                  </Link>{' '}
                  · <Price cents={a.restantDuCentimes} lang={langue} />
                </li>
              ))}
            </ul>
          </section>
          <section className={styles.section}>
            <h2>{label('aCompleter')}</h2>
            <ul>
              {tableau.data.blocages.map((b) => (
                <li key={`${b.factureId}:${b.rang}`}>
                  <Link to={`/${galerieSlug}/artistes/${b.artisteId}`}>{nom(b.artisteId)}</Link> ·{' '}
                  <Link to={`/${galerieSlug}/ventes/${b.factureId}`}>{label('facture')}</Link> ·{' '}
                  {label('ligne')} {b.rang + 1} · {label(`blocages.${b.cause}`)}
                </li>
              ))}
            </ul>
          </section>
        </>
      )}
      <section className={styles.section}>
        <h2>{label('export')}</h2>
        <div className={styles.actions}>
          <Field htmlFor="export-du" label={label('du')}>
            <Input id="export-du" type="date" value={du} onChange={(e) => setDu(e.target.value)} />
          </Field>
          <Field htmlFor="export-au" label={label('auInclus')}>
            <Input id="export-au" type="date" value={au} onChange={(e) => setAu(e.target.value)} />
          </Field>
          <Button onClick={() => void exporter()}>{label('exporter')}</Button>
        </div>
      </section>
      {dialogue && (
        <Dialog
          open
          onOpenChange={(v) => {
            if (!v) setDialogue(null);
          }}
          title={titreDialogue}
          description={label('saisieAide')}
          closeLabel={t('commun:fermer')}
        >
          {dialogue === 'avance' ? (
            <FormulaireAvance
              galerieSlug={galerieSlug}
              artistes={artistes}
              avecCouts={galerieAModule(galerie, 'editions-provenance')}
              avecCoffre={
                galerieAModule(galerie, 'coffre') && galerieALaction(galerie, 'documents:read')
              }
              onCreee={(id) => {
                setDialogue(null);
                navigate(`/${galerieSlug}/comptes-artistes/avances/${id}`);
              }}
            />
          ) : (
            <form
              className={styles.formulaire}
              onSubmit={(e) => {
                e.preventDefault();
                setErreur('');
                const action =
                  dialogue === 'lot'
                    ? lot.mutateAsync({ debut, fin }).then(() => {
                        setDialogue(null);
                        setResultat(label('relevesPrepares'));
                      })
                    : creer.mutateAsync({ artisteId, debut, fin }).then((r) => {
                        setDialogue(null);
                        navigate(`/${galerieSlug}/comptes-artistes/releves/${r.releve.id}`);
                      });
                void action.catch((e: unknown) =>
                  setErreur(messageCompte(e, t('commun:erreurReseau'))),
                );
              }}
            >
              {dialogue === 'releve' && (
                <Field htmlFor="releve-artiste" label={label('artiste')}>
                  <Select
                    id="releve-artiste"
                    value={artisteId}
                    onValueChange={setArtisteId}
                    options={artistes.map((a) => ({ value: a.artisteId, label: a.nom }))}
                  />
                </Field>
              )}
              <Field htmlFor="releve-debut" label={label('debut')}>
                <Input
                  id="releve-debut"
                  type="date"
                  required
                  value={debut}
                  onChange={(e) => setDebut(e.target.value)}
                />
              </Field>
              <Field htmlFor="releve-fin" label={label('finExclue')}>
                <Input
                  id="releve-fin"
                  type="date"
                  required
                  value={fin}
                  onChange={(e) => setFin(e.target.value)}
                />
              </Field>
              {erreur && <p role="alert">{erreur}</p>}
              <Button
                type="submit"
                loading={creer.isPending || lot.isPending}
                disabled={!debut || !fin || debut >= fin || (dialogue === 'releve' && !artisteId)}
              >
                {label('preparer')}
              </Button>
            </form>
          )}
        </Dialog>
      )}
    </div>
  );
}
